import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiMapPin, FiPhone, FiStar } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { hotelService } from '../services/hotelService';
import { ErrorState, LoadingState, PageHero, SectionHeading } from '../components/ui/PageElements';
import { formatCurrency, safeArray } from '../utils/formatters';

const HotelDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  useEffect(() => {
    const fetchHotel = async () => {
      try {
        setLoading(true);
        const data = await hotelService.getHotelById(id);
        setHotel(data);
        setError('');
      } catch (requestError) {
        console.error('Hotel detail error:', requestError);
        setError(t('design.hotelDetail.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchHotel();
  }, [id, t]);

  const photoUrls = useMemo(() => safeArray(hotel?.photoUrls), [hotel?.photoUrls]);

  if (loading) {
    return (
      <Layout>
        <LoadingState label={t('design.hotelDetail.loading')} />
      </Layout>
    );
  }

  if (error || !hotel) {
    return (
      <Layout>
        <ErrorState title={t('design.hotelDetail.unavailableTitle')} message={error || t('design.hotelDetail.unavailableMessage')} action={<Link to="/hotels" className="btn-primary">{t('design.hotelDetail.backToHotels')}</Link>} />
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        eyebrow={t('design.hotelDetail.heroEyebrow')}
        title={hotel.hotelName}
        description={t('design.hotelDetail.heroDescription', { city: hotel.hotelCity || 'Van', district: hotel.district ? `, ${hotel.district}` : '' })}
        actions={
          <>
            <Link to={`/reserve/${hotel.id}`} className="btn-primary">{t('design.hotelDetail.reserve')}</Link>
            <a href={`tel:${hotel.phone || ''}`} className="btn-ghost">
              <FiPhone className="h-4 w-4" />
              {t('design.hotelDetail.callHotel')}
            </a>
          </>
        }
        aside={
          <div className="soft-panel overflow-hidden">
            <div className="relative">
              <img
                src={photoUrls[activePhotoIndex] || '/hotel-placeholder.jpg'}
                alt={hotel.hotelName}
                className="h-[360px] w-full object-cover"
                onError={(event) => {
                  event.currentTarget.src = '/hotel-placeholder.jpg';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3 text-white">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/70">{t('design.hotelDetail.startingPrice')}</p>
                  <p className="mt-2 font-serif text-3xl font-semibold">{formatCurrency(hotel.advancePayment)}</p>
                  <p className="text-sm text-white/80">{t('hotels.perNight')}</p>
                </div>
                {photoUrls.length > 1 ? (
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setActivePhotoIndex((value) => (value === 0 ? photoUrls.length - 1 : value - 1))} className="rounded-full bg-white/90 p-2 text-slate-900">
                      <FiChevronLeft className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => setActivePhotoIndex((value) => (value === photoUrls.length - 1 ? 0 : value + 1))} className="rounded-full bg-white/90 p-2 text-slate-900">
                      <FiChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        }
      />

      <section className="content-width pb-12">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="soft-panel p-6 sm:p-8">
            <SectionHeading
              eyebrow="Otel ozeti"
              title={t('design.hotelDetail.summaryTitle')}
              description={t('design.hotelDetail.summaryDescription')}
            />
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('hotels.location')}</p>
                <p className="mt-3 flex items-center gap-2 text-base text-slate-700">
                  <FiMapPin className="text-teal-700" />
                  {hotel.hotelAddress || `${hotel.hotelCity}, ${hotel.district}`}
                </p>
              </div>
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.hotelDetail.starInfo')}</p>
                <p className="mt-3 flex items-center gap-2 text-base text-slate-700">
                  <FiStar className="text-amber-500" />
                  {t('design.hotelDetail.starValue', { count: hotel.starRating || 0 })}
                </p>
              </div>
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('places.contact')}</p>
                <p className="mt-3 text-base text-slate-700">{hotel.phone || t('design.hotelDetail.contactFallback')}</p>
              </div>
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('hotels.reservation.title')}</p>
                <p className="mt-3 text-base text-slate-700">{t('design.hotelDetail.reservationHint')}</p>
              </div>
            </div>

            {photoUrls.length > 1 ? (
              <div className="mt-8">
                <SectionHeading
                  eyebrow={t('design.hotelDetail.galleryEyebrow')}
                  title={t('design.hotelDetail.galleryTitle')}
                  description={t('design.hotelDetail.galleryDescription')}
                />
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {photoUrls.map((url, index) => (
                    <button
                      key={`${url}-${index}`}
                      type="button"
                      onClick={() => setActivePhotoIndex(index)}
                      className={`overflow-hidden rounded-[24px] border ${index === activePhotoIndex ? 'border-teal-500 shadow-lg shadow-teal-900/10' : 'border-stone-200'}`}
                    >
                      <img
                        src={url}
                        alt={`${hotel.hotelName} ${index + 1}`}
                        className="h-40 w-full object-cover"
                        onError={(event) => {
                          event.currentTarget.src = '/hotel-placeholder.jpg';
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="space-y-6">
            <div className="soft-panel p-6 sm:p-8">
              <p className="eyebrow">Rezervasyon ozeti</p>
              <div className="mt-6 rounded-[28px] border border-stone-200 bg-stone-50/80 p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.hotelDetail.startingPrice')}</p>
                <p className="mt-2 font-serif text-4xl font-semibold text-slate-900">{formatCurrency(hotel.advancePayment)}</p>
                <p className="mt-2 text-sm leading-7 text-slate-600">{t('design.hotelDetail.summaryHint')}</p>
                <Link to={`/reserve/${hotel.id}`} className="btn-primary mt-6 w-full justify-center">{t('design.hotelDetail.continueReservation')}</Link>
              </div>
            </div>

            <div className="soft-panel p-6 sm:p-8">
              <p className="eyebrow">{t('common.note')}</p>
              <h3 className="mt-4 text-2xl font-semibold text-slate-900">{t('design.hotelDetail.noteTitle')}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">
                {t('design.hotelDetail.noteDescription')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HotelDetailPage;
